import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import NotesIcon from '@mui/icons-material/Notes';
import { useWorkspace } from '../store/WorkspaceContext';
import { downloadFileByFormat, downloadFolderZip } from '../utils/download';

const FILE_FORMATS = [
  {
    value: 'txt',
    icon: <DescriptionIcon color="primary" />,
    label: 'Plain Text',
    detail: '.txt',
  },
  {
    value: 'html',
    icon: <CodeIcon color="primary" />,
    label: 'HTML',
    detail: '.html',
  },
  {
    value: 'markdown',
    icon: <NotesIcon color="primary" />,
    label: 'Markdown',
    detail: '.md',
  },
];

const FOLDER_FORMATS = [
  {
    value: 'txt',
    icon: <FolderZipIcon color="primary" />,
    label: 'Folder ZIP',
    detail: '.txt files',
  },
];

function FormatOption({ option, selected }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        width: '100%',
        border: 1,
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 1,
        px: 1.25,
        py: 1,
        bgcolor: selected ? 'action.selected' : 'background.paper',
      }}
    >
      {option.icon}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={800} noWrap>
          {option.label}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {option.detail}
        </Typography>
      </Box>
    </Box>
  );
}

export default function DownloadDialog({ open, item, itemType = 'file', onClose }) {
  const { folders, files } = useWorkspace();
  const [displayItem, setDisplayItem] = useState(item || null);
  const [displayItemType, setDisplayItemType] = useState(itemType);
  const [format, setFormat] = useState('txt');
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const formats = displayItemType === 'folder' ? FOLDER_FORMATS : FILE_FORMATS;

  useEffect(() => {
    if (open) {
      setDisplayItem(item || null);
      setDisplayItemType(itemType);
      setFormat('txt');
      setError('');
      setDownloading(false);
    }
  }, [open, item, itemType]);

  const title = useMemo(() => {
    if (!displayItem) {
      return 'Download';
    }

    return `Download ${displayItem.name}`;
  }, [displayItem]);

  const handleDownload = async () => {
    if (!displayItem) {
      return;
    }

    try {
      setError('');
      setDownloading(true);

      if (displayItemType === 'folder') {
        await downloadFolderZip(displayItem, folders, files, format);
      } else {
        downloadFileByFormat(displayItem, format);
      }

      onClose();
    } catch (downloadError) {
      setError(downloadError.message || 'Unable to prepare this download.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={downloading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <FormControl component="fieldset" fullWidth>
            <RadioGroup value={format} onChange={(event) => setFormat(event.target.value)}>
              <Stack spacing={1}>
                {formats.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={<FormatOption option={option} selected={format === option.value} />}
                    sx={{
                      m: 0,
                      alignItems: 'stretch',
                      '& .MuiFormControlLabel-label': {
                        width: '100%',
                      },
                    }}
                  />
                ))}
              </Stack>
            </RadioGroup>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={downloading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleDownload} disabled={downloading}>
          {downloading ? 'Preparing...' : 'Download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
